import type { CalculatedField, CalculatedFieldOperation } from './hooks/useReportBuilder';

export function sanitizeCalculatedFieldAlias(name: string, label?: string): string {
  const raw = (name.trim() || label?.trim() || 'calculated').replace(/[^a-zA-Z0-9_]/g, '_');
  return raw.slice(0, 100) || 'calculated';
}

function coalesceField(field: string, defaultValue: 0 | 1): string {
  return `COALESCE(${field}, ${defaultValue})`;
}

const CALC_UNAVAILABLE = '0';

function fieldIsNull(field: string): string {
  return `${field} IS NULL`;
}

function divisorIsZero(field: string): string {
  return `${coalesceField(field, 0)} = 0`;
}

function safeCalculatedCase(whenClauses: string[], elseSql: string): string {
  return `CASE WHEN ${whenClauses.join(' OR ')} THEN ${CALC_UNAVAILABLE} ELSE ${elseSql} END`;
}

export function uiCalculatedFieldToApi(entry: {
  name: string;
  label?: string;
  operation: CalculatedFieldOperation;
  fieldA: string;
  fieldB: string;
}): { alias: string; expression: string } {
  const alias = sanitizeCalculatedFieldAlias(entry.name, entry.label);
  const { fieldA, fieldB, operation } = entry;

  let expression: string;
  switch (operation) {
    case 'add':
      expression = safeCalculatedCase(
        [fieldIsNull(fieldA), fieldIsNull(fieldB)],
        `${coalesceField(fieldA, 0)} + ${coalesceField(fieldB, 0)}`,
      );
      break;
    case 'subtract':
      expression = safeCalculatedCase(
        [fieldIsNull(fieldA), fieldIsNull(fieldB)],
        `${coalesceField(fieldA, 0)} - ${coalesceField(fieldB, 0)}`,
      );
      break;
    case 'multiply':
      expression = safeCalculatedCase(
        [fieldIsNull(fieldA), fieldIsNull(fieldB)],
        `${coalesceField(fieldA, 0)} * ${coalesceField(fieldB, 0)}`,
      );
      break;
    case 'divide':
      expression = safeCalculatedCase(
        [fieldIsNull(fieldA), fieldIsNull(fieldB), divisorIsZero(fieldB)],
        `${coalesceField(fieldA, 0)} / ${coalesceField(fieldB, 0)}`,
      );
      break;
    case 'percentage':
      expression = safeCalculatedCase(
        [fieldIsNull(fieldA), fieldIsNull(fieldB), divisorIsZero(fieldB)],
        `(${coalesceField(fieldA, 0)} / ${coalesceField(fieldB, 0)}) * 100`,
      );
      break;
    default:
      expression = safeCalculatedCase(
        [fieldIsNull(fieldA), fieldIsNull(fieldB)],
        `${coalesceField(fieldA, 0)} + ${coalesceField(fieldB, 0)}`,
      );
  }

  return { alias, expression };
}

const COALESCE_OPERAND = 'COALESCE\\(([^,]+),\\s*0\\)';

/** Reverse API expression into UI operands (report-builder generated SQL). */
export function parseCalculatedFieldExpression(expression: string): {
  operation: CalculatedFieldOperation;
  fieldA: string;
  fieldB: string;
} | null {
  const elseMatch = expression.match(/ELSE (.+) END\s*$/i);
  if (!elseMatch) return null;
  const elseSql = elseMatch[1].trim();

  const pct = new RegExp(`\\(${COALESCE_OPERAND} \\/ ${COALESCE_OPERAND}\\) \\* 100`);
  let match = elseSql.match(pct);
  if (match) {
    return { operation: 'percentage', fieldA: match[1].trim(), fieldB: match[2].trim() };
  }

  const binaryOps: [CalculatedFieldOperation, string][] = [
    ['add', `${COALESCE_OPERAND} \\+ ${COALESCE_OPERAND}`],
    ['subtract', `${COALESCE_OPERAND} - ${COALESCE_OPERAND}`],
    ['multiply', `${COALESCE_OPERAND} \\* ${COALESCE_OPERAND}`],
    ['divide', `${COALESCE_OPERAND} \\/ ${COALESCE_OPERAND}`],
  ];

  for (const [operation, pattern] of binaryOps) {
    match = elseSql.match(new RegExp(pattern));
    if (match) {
      return { operation, fieldA: match[1].trim(), fieldB: match[2].trim() };
    }
  }

  return null;
}

export function isStoredApiCalculatedField(
  cf: Partial<CalculatedField>,
): cf is CalculatedField & { alias: string; expression: string } {
  return (
    typeof cf.alias === 'string' &&
    cf.alias.length > 0 &&
    typeof cf.expression === 'string' &&
    cf.expression.length > 0
  );
}

function hasCompleteCalculatedFieldUi(cf: CalculatedField): boolean {
  return (
    !!(cf.name.trim() || cf.label.trim()) &&
    !!cf.fieldA.trim() &&
    !!cf.fieldB.trim() &&
    !!cf.operation
  );
}

/** Keep alias/expression in sync whenever UI operands or name change. */
export function syncCalculatedField(cf: CalculatedField): CalculatedField {
  if (hasCompleteCalculatedFieldUi(cf)) {
    const api = uiCalculatedFieldToApi({
      name: cf.name.trim() || sanitizeCalculatedFieldAlias('', cf.label),
      label: cf.label,
      operation: cf.operation,
      fieldA: cf.fieldA,
      fieldB: cf.fieldB,
    });
    return { ...cf, name: api.alias, alias: api.alias, expression: api.expression };
  }

  const next = { ...cf };
  delete next.alias;
  delete next.expression;
  return next;
}
